import {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Control,
  Controller,
  UseFormSetValue,
  useFormContext,
  useWatch,
} from 'react-hook-form';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore framer-motion is in the parent dir
import { AnimatePresence, motion } from 'framer-motion';
import _ from 'lodash';
import {
  ArrowRightIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CircleAlertIcon,
  CopyIcon,
  InfoIcon,
  type LucideIcon,
  MapIcon,
  PencilIcon,
  RefreshCcwIcon,
  TrashIcon,
  XIcon,
} from 'lucide-react';

import { Checkbox } from 'components/Checkbox';
import { GenericLineChart } from 'components/GenericLineChart';
import { Label } from 'components/Label';
import { LaneHistoryFromServiceChart } from 'components/LaneHistoryFromServiceChart';
import { QuoteCard, QuoteCardType } from 'components/QuoteCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Tooltip';
import { Flex } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import CarrierPriceCalculator, {
  CarrierPriceCalculatorParent,
} from 'components/pricing/CarrierPriceCalculator';
import { Typography } from 'components/typography';
import { QuickQuoteConfig } from 'contexts/serviceContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useIsTqlInbox } from 'hooks/useIsTqlInbox';
import { useServiceFeatures } from 'hooks/useServiceContext';
import {
  LaneHistoryResponse,
  SourceHistory,
  WeekLaneData,
} from 'lib/api/getLaneHistory';
import { LaneHistoryFromServiceResponse } from 'lib/api/getLaneHistoryFromService';
import {
  QuoteTerminatingAction,
  QuotedValueTypeApi,
  SubmitFinalQuoteBody,
  buildUserConfirmedQuoteBody,
  quoteValueTypeToApi,
} from 'lib/api/updateQuoteRequestSuggestion';
import { Maybe, Undef } from 'types/UtilityTypes';
import {
  Quoting,
  QuotingPortals,
  TMS,
  integrationNameMap,
} from 'types/enums/Integrations';
import captureException from 'utils/captureException';
import { copyToClipboard } from 'utils/copyToClipboard';
import { formatCurrency } from 'utils/formatCurrency';
import { titleCase } from 'utils/formatStrings';
import { constructGoogleMapsUrl } from 'utils/googleMaps';
import {
  chartConfig,
  chartConfigPercentile,
} from 'utils/laneHistoryChartConfig';
import {
  calculatePricing,
  sellQuoteUnitsFromTripTotal,
  tripTotalFromSellQuoteUnits,
} from 'utils/priceCalculations';
import { cn } from 'utils/shadcn';

import { EquipmentInfoTooltip } from '../EquipmentInfoTooltip';
import { getLaneHistoryChartDescription } from '../LaneHistory/laneHistoryDescription';
import { QuickQuoteLocationInput } from '../common/QuickQuoteLocationInput';
import { QuoteValueType } from '../constants';
import { useQuoteConfirmationPingWithValues } from '../hooks/useQuoteConfirmationPing';
import {
  BatchQuoteFormInput,
  BatchQuoteFormValues,
  BatchQuoteResult,
  CarrierCostType,
  LaneTier,
  ProfitType,
  QuoteFormStop,
  SelectedQuoteType,
  SubTransportTypeOption,
  TransportTypeOption,
  getParentTransportTypeForSubTransportType,
  getSubTransportTypeSelectOptions,
} from '../types';
import {
  formatStopsDisplay,
  getDefaultLaneHistoryTierIndex,
  getFallbackTierBadge,
  handleQuoteLaneHistory,
  selectDefaultQuoteCard,
} from '../utils';
import {
  getMileageSourceLabel,
  resolveQuoteMileageSource,
} from '../utils/mileageSource';
import { TierFallbackBadge } from '../utils/quoteCards';
import { BatchLaneStopsEditor } from './BatchLaneStopsEditor';

export type DistanceSource = Quoting | QuotingPortals | 'pcmiler';

export interface BatchQuoteInputCardProps {
  index: number;
  control: Control<BatchQuoteFormValues>;
  transportTypeOptions: TransportTypeOption[];
  subTransportTypeOptions?: SubTransportTypeOption[];
  isSubTransportTypesEnabled?: boolean;
  onRemove: (index: number) => void | Promise<void>;
  result: Undef<BatchQuoteResult>;
  laneHistoryResult: Undef<LaneHistoryResponse>;
  laneHistoryFromServiceResult?: Undef<LaneHistoryFromServiceResponse>;
  setValue: UseFormSetValue<BatchQuoteFormValues>;
  isSelected: boolean;
  onSelect: (isSelected: boolean) => void;
  quickQuoteConfig: Undef<QuickQuoteConfig>;
  isExpanded: boolean;
  onToggleExpand: () => void;
  registerCarrierSetter?: (
    index: number,
    setter: (carrier: SelectedQuoteType) => void
  ) => void;
  registerPriceCallback?: (
    index: number,
    priceData: Maybe<{
      finalPrice: number;
      maxDistance: number;
      fuelEstimate: number;
    }>
  ) => void;
  registerPriceFormatSetter?: (
    index: number,
    setter: (priceFormat: CarrierCostType | 'Both') => void
  ) => void;
  onPriceFormatChange?: (
    index: number,
    priceFormat: CarrierCostType | 'Both'
  ) => void;
  isTMSLaneHistoryEnabled: boolean;
  isQuoteLaneHistoryEnabled: boolean;
  isGetLaneRateFromServiceEnabled?: boolean;
  onRetryQuote?: (index: number) => void;
  isRetrying?: boolean;
  onSelectedQuoteIdChange?: (
    laneIndex: number,
    quoteId: number | undefined
  ) => void;
  onLaneTerminatingAction?: (params: {
    quoteRequestId: number;
    finalQuoteData: SubmitFinalQuoteBody;
    selectedQuickQuoteId?: number;
    action: QuoteTerminatingAction;
    suggestionId?: number;
    quotedValueType?: QuotedValueTypeApi;
  }) => Promise<void>;
}

function getBatchQuoteCollapsedSellRateLabel(
  priceFormat: CarrierCostType | 'Both',
  isTql: boolean
): string {
  if (priceFormat === CarrierCostType.Linehaul) {
    return 'Linehaul Rate';
  }
  if (isTql) {
    return 'Bill Rate';
  }
  return 'Sell Rate';
}

function getBatchQuoteCardBorderClass(
  hasQuoteError: boolean,
  isSelected: boolean
): string {
  if (hasQuoteError) {
    return 'border-error-300 bg-linear-to-br from-error-50 to-error-100/75';
  }
  if (isSelected) {
    return 'border-info-500 bg-linear-to-br from-info-50 to-info-100/75';
  }
  return 'border-neutral-500 bg-linear-to-br from-neutral-50 to-info-50/50';
}

function getLaneCheckboxClass(
  hasQuoteError: boolean,
  isSelected: boolean
): string {
  if (hasQuoteError) {
    return 'border-error-400';
  }
  if (isSelected) {
    return '';
  }
  return 'group-hover:bg-neutral-600 group-hover:border-neutral-600';
}

function getLaneIndexLabelClass(
  hasQuoteError: boolean,
  isSelected: boolean
): string {
  if (hasQuoteError) {
    return 'text-error-600';
  }
  if (isSelected) {
    return 'text-neutral-50';
  }
  return 'text-neutral-500 group-hover:text-neutral-50';
}

export const BatchQuoteInputCard = ({
  index,
  control,
  transportTypeOptions,
  subTransportTypeOptions = [],
  isSubTransportTypesEnabled = false,
  onRemove,
  result,
  laneHistoryResult,
  laneHistoryFromServiceResult,
  setValue,
  isSelected,
  onSelect,
  isExpanded,
  onToggleExpand,
  registerCarrierSetter,
  registerPriceCallback,
  registerPriceFormatSetter,
  onPriceFormatChange,
  isTMSLaneHistoryEnabled,
  isQuoteLaneHistoryEnabled,
  isGetLaneRateFromServiceEnabled,
  onRetryQuote,
  isRetrying = false,
  onSelectedQuoteIdChange,
  onLaneTerminatingAction,
  quickQuoteConfig,
}: BatchQuoteInputCardProps) => {
  const values = useWatch({
    control,
    name: `quotes.${index}`,
  });

  const { profit, profitType } = values;

  const {
    currentState: { openExternalUrl },
  } = useContext(SidebarStateContext);

  const isTql = useIsTqlInbox();
  const pickupLabel = isTql ? 'Pick' : 'Pickup';
  const dropoffLabel = isTql ? 'Drop' : 'Dropoff';

  const {
    formState: { errors },
    getValues,
  } = useFormContext();

  const hasQuoteError =
    errors.quotes && Array.isArray(errors.quotes)
      ? errors.quotes[index]?.root
      : undefined;

  const {
    serviceFeaturesEnabled: {
      isQuoteCalculatorMarginEnabled,
      isMultiStopQuickQuoteEnabled,
    },
    quotingIntegrations,
    tmsIntegrations,
  } = useServiceFeatures();

  // Proper price calculation with margin/markup logic
  const calculateFinalPrice = useCallback(
    (
      carrierCost: number,
      carrierCostType: CarrierCostType,
      profit: number,
      profitType: ProfitType,
      distance: number
    ): number => {
      // Calculate flat carrier cost
      const flatCarrierCost =
        carrierCostType === CarrierCostType.Flat
          ? carrierCost
          : carrierCost * distance;

      if (profitType === ProfitType.Amount) {
        // Flat profit: Add the profit to the carrier cost
        return flatCarrierCost + profit;
      } else {
        // ProfitType.Percentage
        if (isQuoteCalculatorMarginEnabled) {
          // Margin formula: Price = Cost / (1 - Profit_Percentage)
          if (profit >= 100) {
            // Profit margin cannot be 100% or more.
            return flatCarrierCost / (1 - 0.999);
          } else {
            return flatCarrierCost / (1 - profit / 100);
          }
        } else {
          // Markup formula: Price = Cost * (1 + Markup_Percentage)
          return flatCarrierCost * (1 + profit / 100);
        }
      }
    },
    [isQuoteCalculatorMarginEnabled]
  );

  // Check if this is an empty card (no locations filled). For multi-stop lanes
  // we treat the card as empty only when every stop is blank so a partially
  // filled intermediate stop still counts as work-in-progress.
  const isEmptyCard = isMultiStopQuickQuoteEnabled
    ? !values?.stops?.some((stop: QuoteFormStop) => Boolean(stop?.location))
    : !values?.stops?.[0]?.location && !values?.stops?.[1]?.location;

  const [isEditing, setIsEditing] = useState(isEmptyCard);
  const [originalValues, setOriginalValues] =
    useState<BatchQuoteFormInput | null>(null);
  const [hasCopied, setHasCopied] = useState(false);
  const copyTimeoutRef = useRef<Maybe<NodeJS.Timeout>>(null);

  const [selectedCarrier, setSelectedCarrier] = useState<SelectedQuoteType>(
    SelectedQuoteType.GS_NETWORK
  );
  const [carrierCost, setCarrierCost] = useState(0);
  const [carrierCostType, setCarrierCostType] = useState<CarrierCostType>(
    CarrierCostType.Flat
  );
  /** Trip-level sell total ($); display, copy, and draft use this — not CPC quote units. */
  const [finalPrice, setFinalPrice] = useState<Maybe<number>>(null);
  const [maxDistance, setMaxDistance] = useState(0);

  const calculatorFinalPrice = useMemo(() => {
    if (finalPrice == null) {
      return finalPrice;
    }
    return sellQuoteUnitsFromTripTotal(
      finalPrice,
      carrierCostType,
      maxDistance
    );
  }, [finalPrice, carrierCostType, maxDistance]);

  const setFinalPriceFromCalculator = useCallback<
    Dispatch<SetStateAction<Maybe<number>>>
  >(
    (value) => {
      setFinalPrice((prevTripTotal) => {
        const prevQuoteUnits = sellQuoteUnitsFromTripTotal(
          prevTripTotal ?? 0,
          carrierCostType,
          maxDistance
        );
        const resolved =
          typeof value === 'function' ? value(prevQuoteUnits) : value;
        if (resolved == null) {
          return resolved;
        }
        return tripTotalFromSellQuoteUnits(
          resolved,
          carrierCostType,
          maxDistance
        );
      });
    },
    [carrierCostType, maxDistance]
  );
  const [datFuelSurcharge, setDATFuelSurcharge] = useState<Maybe<number>>(null);
  const [fuelEstimate, setFuelEstimate] = useState(0);
  const [distanceSource, setDistanceSource] =
    useState<Maybe<DistanceSource>>(null);
  const [priceFormat, setPriceFormat] = useState<CarrierCostType | 'Both'>(
    CarrierCostType.Flat
  );

  // Lane history state
  const [tmsLaneHistory, setTMSLaneHistory] =
    useState<Maybe<SourceHistory[]>>(null);
  const [tmsLaneHistorySelectedTierIndex, setTmsLaneHistorySelectedTierIndex] =
    useState<Maybe<number>>(null);

  const setProfitHandler = useCallback(
    (newProfit: number) => {
      setValue(`quotes.${index}.profit`, newProfit);
    },
    [index, setValue]
  );

  const setProfitTypeHandler = useCallback(
    (newProfitType: ProfitType) => {
      setValue(`quotes.${index}.profitType`, newProfitType);
    },
    [index, setValue]
  );

  // Track if we've already set the initial carrier selection to avoid resetting it
  const [hasSetInitialCarrier, setHasSetInitialCarrier] = useState(false);

  // Reset the initial carrier flag when a new quote result arrives.
  useEffect(() => {
    if (result?.response?.quoteRequestId) {
      setHasSetInitialCarrier(false);
    }
    // quoteRequestId is stable across parent re-renders; response object identity is not.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [result?.response?.quoteRequestId]);

  // Generate combined quote cards (original + lane history)
  const allQuoteCards = useMemo(() => {
    if (!result?.cards) return [];

    let combinedCards = [...result.cards];

    // Add lane history cards if available
    if (laneHistoryResult) {
      const laneHistoryCards = handleQuoteLaneHistory(
        laneHistoryResult,
        values.stops
      );
      combinedCards = [...combinedCards, ...laneHistoryCards];
    }

    return combinedCards;
  }, [result?.cards, laneHistoryResult, values.stops]);

  const selectedQuickQuoteIdFromCard = allQuoteCards.find(
    (c) => c.type === selectedCarrier
  )?.quoteId;

  const kencoAverageQuote = allQuoteCards.find(
    (q) => q.type === SelectedQuoteType.KENCO_FLASH_AVERAGE
  );

  const kencoFuelSurcharge = useMemo((): Maybe<number> => {
    if (!kencoAverageQuote?.kencoMetadata) {
      return null;
    }

    return kencoAverageQuote.kencoMetadata.fuel_cost_per_mile ?? null;
  }, [kencoAverageQuote]);

  const customerTableFuelSurcharge = useMemo(
    (): Maybe<number> =>
      result?.response?.configuration?.customerTableFscPerMile ?? null,
    [result?.response?.configuration]
  );

  useEffect(() => {
    if (selectedCarrier !== SelectedQuoteType.KENCO_FLASH_AVERAGE) {
      return;
    }

    if (!kencoAverageQuote?.kencoMetadata) {
      return;
    }

    const marginPercentage =
      kencoAverageQuote.kencoMetadata.average_margin_pct ?? 0;
    setProfitTypeHandler(ProfitType.Percentage);
    setProfitHandler(Math.round(marginPercentage * 100) / 100);
  }, [
    selectedCarrier,
    kencoAverageQuote,
    setProfitHandler,
    setProfitTypeHandler,
  ]);

  useEffect(() => {
    onSelectedQuoteIdChange?.(index, selectedQuickQuoteIdFromCard);
  }, [index, selectedQuickQuoteIdFromCard, onSelectedQuoteIdChange]);

  const laneQuoteRequestId = result?.response?.quoteRequestId ?? 0;
  useQuoteConfirmationPingWithValues(
    laneQuoteRequestId,
    getValues,
    () => selectedQuickQuoteIdFromCard,
    {
      carrierCost,
      carrierCostType,
      profit: profit ?? 0,
      profitType,
      finalFlatPrice: finalPrice ?? null,
      maxDistance,
    },
    { readyForDefaultPing: hasSetInitialCarrier }
  );

  useEffect(() => {
    // When the result comes in, we need to derive multiple state values.
    // Consolidating them here prevents a flicker/delay where the price
    // would first show a value based on old state, then update.

    if (!result?.response) {
      // If there's no result, just calculate the price with the current state.
      const newFinalPrice = calculateFinalPrice(
        carrierCost,
        carrierCostType,
        profit,
        profitType,
        maxDistance
      );
      setFinalPrice(newFinalPrice);
      return;
    }

    const newMaxDistance =
      result.response.quotes?.reduce(
        (max, current) => (current.distance > max.distance ? current : max),
        { distance: 0 }
      ).distance || 0;

    // Set distance source from the quote with maximum distance
    const maxDistanceQuote = result.response.quotes?.length
      ? result.response.quotes.reduce((max, current) =>
          current.distance > max.distance ? current : max
        )
      : null;
    const newDistanceSource = maxDistanceQuote
      ? resolveQuoteMileageSource(maxDistanceQuote)
      : null;

    let newCarrierCost = carrierCost;
    if (allQuoteCards?.length && !hasSetInitialCarrier) {
      const hasLaneHistoryResults = Boolean(
        laneHistoryResult &&
        Object.values(laneHistoryResult.resultsBySource ?? {}).length > 0
      );

      const selectedCard = selectDefaultQuoteCard({
        quoteCards: allQuoteCards,
        defaultFTLBuyRateSource: quickQuoteConfig?.defaultFTLBuyRateSource,
        isTMSLaneHistoryEnabled,
        hasLaneHistoryResults,
      });

      if (selectedCard) {
        setSelectedCarrier(selectedCard.type);
        newCarrierCost = Math.round(selectedCard.cost * 100) / 100;
        setCarrierCost(newCarrierCost);
        setHasSetInitialCarrier(true);
      }
    }

    const newFinalPrice = calculateFinalPrice(
      newCarrierCost,
      carrierCostType,
      profit,
      profitType,
      newMaxDistance
    );

    setMaxDistance(newMaxDistance);
    setDistanceSource(newDistanceSource);
    setDATFuelSurcharge((result.response as any).datFuelSurcharge || null);
    setFinalPrice(newFinalPrice);
    // carrierCost, profit, and maxDistance are read when result/carrier init changes;
    // omitting them avoids overwriting bill-rate finalPrice (incl. accessorials) on margin edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above
  }, [
    result,
    carrierCostType,
    hasSetInitialCarrier,
    calculateFinalPrice,
    allQuoteCards,
    isTMSLaneHistoryEnabled,
    laneHistoryResult,
    quickQuoteConfig?.defaultFTLBuyRateSource,
  ]);

  // Notify parent of price changes
  useEffect(() => {
    if (result?.response && finalPrice !== null && registerPriceCallback) {
      registerPriceCallback(index, {
        finalPrice,
        maxDistance,
        fuelEstimate,
      });
    }
  }, [
    finalPrice,
    maxDistance,
    fuelEstimate,
    result?.response,
    registerPriceCallback,
    index,
  ]);

  // Start in editing mode if this is an empty card
  useEffect(() => {
    if (isEmptyCard && !isEditing) {
      setIsEditing(true);
    }
  }, [isEmptyCard, isEditing]);

  // Default TMS Lane History Tier hierarchy: Market-to-Market -> 3-Digit Zip -> first available tier
  useEffect(() => {
    const tmsHistoriesKey = Object.keys(
      laneHistoryResult?.resultsBySource || {}
    ).find((source) => Object.values(TMS).includes(source as TMS));

    if (!tmsHistoriesKey) {
      return;
    }

    const laneHistoryResults =
      laneHistoryResult?.resultsBySource[tmsHistoriesKey as TMS];
    setTMSLaneHistory(laneHistoryResults || null);

    setTmsLaneHistorySelectedTierIndex(
      laneHistoryResults?.length
        ? getDefaultLaneHistoryTierIndex(laneHistoryResults)
        : 0
    );
  }, [laneHistoryResult]);

  const toggleLaneHistoryGraph = (option: string) => {
    if (!tmsLaneHistory) return;

    const tierIndex = tmsLaneHistory?.findIndex(
      (history) => history.laneTier === option
    );
    if (tierIndex === -1) return;

    setTmsLaneHistorySelectedTierIndex(tierIndex);
  };

  /**
   * User-confirmed "copy" for this lane (header copy button): PATCH quote request for metrics
   * with linehaul vs total_sell from the lane price-format toggle. Runs in the background;
   * the click handler copies the formatted price to clipboard separately.
   */
  const handleLaneTerminatingAction_Copy = useCallback(() => {
    if (!result?.response || !onLaneTerminatingAction) return;
    const { flatCarrierCost, finalProfit } = calculatePricing(
      carrierCost,
      carrierCostType,
      profit ?? 0,
      profitType,
      maxDistance
    );
    const quoteValueTypeForLane =
      priceFormat === CarrierCostType.Linehaul
        ? QuoteValueType.Linehaul
        : QuoteValueType.TotalSell;
    const quotedValueType = quoteValueTypeToApi(quoteValueTypeForLane);
    const body = buildUserConfirmedQuoteBody({
      finalQuotePrice: _.round(finalPrice ?? 0),
      finalMargin: _.round(finalProfit),
      marginType: profitType,
      finalCarrierCost: _.round(flatCarrierCost),
      carrierCostType,
      quoteTerminatingAction: QuoteTerminatingAction.Copy,
      quotedValueType,
    });
    try {
      onLaneTerminatingAction({
        quoteRequestId: result.response.quoteRequestId,
        finalQuoteData: body,
        selectedQuickQuoteId: allQuoteCards.find(
          (c) => c.type === selectedCarrier
        )?.quoteId,
        action: QuoteTerminatingAction.Copy,
        suggestionId: values.suggestionId,
        quotedValueType,
      });
    } catch (err) {
      captureException(err, {
        functionName: 'handleLaneTerminatingAction_Copy',
      });
    }
  }, [
    result?.response,
    onLaneTerminatingAction,
    finalPrice,
    profit,
    profitType,
    carrierCost,
    carrierCostType,
    maxDistance,
    priceFormat,
    allQuoteCards,
    selectedCarrier,
    values.suggestionId,
  ]);

  const handlers = {
    edit: () => {
      setOriginalValues(values);
      setIsEditing(true);
    },
    save: () => {
      setIsEditing(false);
      setOriginalValues(null);
    },
    discard: () => {
      if (originalValues) {
        setValue(`quotes.${index}`, originalValues);
      }
      setIsEditing(false);
      setOriginalValues(null);
    },
    remove: () => onRemove(index),
  };

  const ActionButton = ({
    onClick,
    className,
    icon: Icon,
    'aria-label': ariaLabel,
    tooltip,
  }: {
    onClick: () => void;
    className: string;
    icon: LucideIcon;
    'aria-label': string;
    tooltip?: string;
  }) => {
    const button = (
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onClick}
        className={`p-1 rounded-full transition-colors hover:cursor-pointer ${className}`}
        type='button'
        aria-label={ariaLabel}
      >
        <Icon className='h-4 w-4' />
      </motion.button>
    );

    return tooltip ? (
      <Tooltip delayDuration={100} disableHoverableContent>
        <TooltipTrigger asChild>{button}</TooltipTrigger>
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    ) : (
      button
    );
  };

  const removeActionLabel =
    values.suggestionId != null ? 'Dismiss suggestion' : 'Remove lane';
  const removeActionTooltip =
    values.suggestionId != null
      ? 'Dismiss this suggestion and remove it from the batch'
      : undefined;

  const renderActions = () => (
    <AnimatePresence mode='wait'>
      {isEditing ? (
        <motion.div
          key='editing-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          {!isEmptyCard ? (
            <>
              <ActionButton
                onClick={handlers.save}
                className='hover:bg-info-200 text-info-600'
                icon={CheckIcon}
                aria-label='Save changes'
              />
              <ActionButton
                onClick={handlers.discard}
                className='hover:bg-error-200 text-error-600'
                icon={XIcon}
                aria-label='Discard changes'
              />
            </>
          ) : (
            <ActionButton
              onClick={handlers.remove}
              className='hover:bg-error-300 text-error-600'
              icon={TrashIcon}
              aria-label={removeActionLabel}
              tooltip={removeActionTooltip}
            />
          )}
        </motion.div>
      ) : (
        <motion.div
          key='view-actions'
          {...ANIMATIONS.actions}
          className='flex gap-1.5'
        >
          {!result?.response ? (
            <ActionButton
              onClick={handlers.edit}
              className='hover:bg-info-300 text-info-600'
              icon={PencilIcon}
              aria-label='Edit lane'
            />
          ) : (
            <ActionButton
              onClick={async () => {
                try {
                  const success = await copyToClipboard(
                    formatPriceForCopy(
                      finalPrice,
                      priceFormat,
                      maxDistance,
                      fuelEstimate
                    )
                  );

                  handleLaneTerminatingAction_Copy();

                  if (success) {
                    setHasCopied(true);
                    if (copyTimeoutRef.current) {
                      clearTimeout(copyTimeoutRef.current);
                    }
                    copyTimeoutRef.current = setTimeout(() => {
                      setHasCopied(false);
                      copyTimeoutRef.current = null;
                    }, 2000);
                  }
                } catch (error) {
                  captureException(error, {
                    functionName: 'BatchQuoteInputCard copy quote price',
                  });
                }
              }}
              className='hover:bg-info-300 text-info-600'
              icon={hasCopied ? CheckIcon : CopyIcon}
              aria-label='Copy quote price'
            />
          )}
          <ActionButton
            onClick={handlers.remove}
            className='hover:bg-error-400 text-error-600'
            icon={TrashIcon}
            aria-label={removeActionLabel}
            tooltip={removeActionTooltip}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );

  // Handle Enter key to save batch quote item without submitting the parent form
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      e.stopPropagation();
      handlers.save();
    }
  };

  const useGranularEquipmentSelect =
    isSubTransportTypesEnabled && subTransportTypeOptions.length > 0;

  const equipmentSelectOptions = getSubTransportTypeSelectOptions(
    subTransportTypeOptions
  );

  const getEquipmentLabel = () => {
    if (useGranularEquipmentSelect && values?.subTransportType) {
      return (
        subTransportTypeOptions.find(
          (option) => option.value === values.subTransportType
        )?.label ?? ''
      );
    }

    return (
      transportTypeOptions.find(
        (option) => option.value === values?.transportType
      )?.label ?? ''
    );
  };

  const renderEditingForm = () => (
    <motion.div
      key='editing-form'
      {...ANIMATIONS.toggle}
      className='overflow-hidden w-full'
      onKeyDown={handleKeyDown}
    >
      <div className='mb-3'>
        <div className='flex items-center gap-x-1'>
          <Label
            name={
              useGranularEquipmentSelect
                ? `quotes.${index}.subTransportType`
                : `quotes.${index}.transportType`
            }
            required={true}
            className='text-xs'
          >
            {isTql ? 'Equipment Type' : 'Transport Type'}
          </Label>
          {useGranularEquipmentSelect && (
            <EquipmentInfoTooltip
              quotingIntegrations={quotingIntegrations}
              tmsIntegrations={tmsIntegrations}
            />
          )}
        </div>
        {useGranularEquipmentSelect ? (
          <Controller
            name={`quotes.${index}.subTransportType`}
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }) => (
              <Select
                options={equipmentSelectOptions}
                showSearch
                onChange={(value) => {
                  const selectedValue = String(value);
                  field.onChange(selectedValue);
                  const parentTransportType =
                    getParentTransportTypeForSubTransportType(
                      selectedValue,
                      subTransportTypeOptions
                    );
                  if (parentTransportType) {
                    setValue(
                      `quotes.${index}.transportType`,
                      parentTransportType,
                      { shouldValidate: true }
                    );
                  }
                }}
                value={field.value ?? ''}
              >
                <SelectTrigger className='mt-1 bg-neutral-50 border-neutral-500 hover:border-brand-main transition-colors text-xs'>
                  <SelectValue placeholder='Choose' />
                </SelectTrigger>
                <SelectContent />
              </Select>
            )}
          />
        ) : (
          <Controller
            name={`quotes.${index}.transportType`}
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className='mt-1 bg-neutral-50 border-neutral-500 hover:border-brand-main transition-colors text-xs'>
                  <SelectValue placeholder='Choose' />
                </SelectTrigger>
                <SelectContent>
                  {transportTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        )}
      </div>

      {isMultiStopQuickQuoteEnabled ? (
        <BatchLaneStopsEditor index={index} control={control} />
      ) : (
        <Flex justify='between' align='start' gap='xs' className='w-full mx-0'>
          <div className='w-full'>
            <QuickQuoteLocationInput
              name={`quotes.${index}.stops.0.location`}
              label={pickupLabel}
              placeholder='City, State or ZIP'
              labelClassName='text-xs'
              inputClassName='text-xs tracking-tight bg-neutral-50 border-neutral-500'
              required
            />
          </div>

          <div className='flex items-center justify-center mt-7'>
            <ArrowRightIcon className='w-4 h-4 text-neutral-800' />
          </div>

          <div className='w-full'>
            <QuickQuoteLocationInput
              name={`quotes.${index}.stops.1.location`}
              label={dropoffLabel}
              placeholder='City, State or ZIP'
              labelClassName='text-xs'
              inputClassName='text-xs tracking-tight bg-neutral-50 border-neutral-500'
              required
            />
          </div>
        </Flex>
      )}
    </motion.div>
  );

  const renderViewMode = () => {
    const stops = values?.stops ?? [];
    const hasMoreThanTwoStops = stops.length > 2;

    return (
      <motion.div
        key='view-mode'
        {...ANIMATIONS.toggle}
        className='overflow-hidden w-full'
        onClick={handlers.edit}
      >
        {hasMoreThanTwoStops ? (
          <Flex
            align='center'
            justify='start'
            gap='sm'
            className='text-xs text-neutral-900'
          >
            <span className='font-medium'>{formatStopsDisplay(stops)}</span>
          </Flex>
        ) : (
          <Flex
            align='center'
            justify='start'
            gap='sm'
            className='text-xs text-neutral-900'
          >
            <span className='font-medium'>{stops[0]?.location ?? ''}</span>
            <ArrowRightIcon className='w-3.5 h-3.5 text-neutral-800' />
            <span className='font-medium'>{stops[1]?.location ?? ''}</span>
          </Flex>
        )}
      </motion.div>
    );
  };

  const formatFinalPriceDisplay = (
    price: Maybe<number>,
    format: CarrierCostType | 'Both',
    distance: number,
    fuelEstimate: number
  ): string => {
    const finalPrice = price ?? 0;

    if (format === CarrierCostType.Linehaul && fuelEstimate) {
      return formatCurrency(finalPrice - fuelEstimate, 'USD');
    }

    if (!distance) return formatCurrency(finalPrice, 'USD');

    if (format === 'Both') {
      return `${formatCurrency(finalPrice, 'USD')} (${formatCurrency(
        finalPrice / distance,
        'USD',
        2
      )}/mile)`;
    } else if (format === CarrierCostType.PerMile) {
      return `${formatCurrency(finalPrice / distance, 'USD', 2)}/mi`;
    } else {
      return formatCurrency(finalPrice, 'USD');
    }
  };

  const formatPriceForCopy = (
    price: Maybe<number>,
    format: CarrierCostType | 'Both',
    distance: number,
    fuelEstimate: number
  ): string => {
    const finalPrice = price ?? 0;

    if (format === CarrierCostType.Linehaul && fuelEstimate) {
      const linehaulPrice = finalPrice - fuelEstimate;
      return linehaulPrice.toFixed(0);
    }

    if (!distance) return finalPrice.toFixed(0);

    if (format === 'Both') {
      // For "Both" format, keep the dollar formatting as requested
      return `${formatCurrency(finalPrice, 'USD')} (${formatCurrency(
        finalPrice / distance,
        'USD',
        2
      )}/mile)`;
    } else if (format === CarrierCostType.PerMile) {
      const perMilePrice = finalPrice / distance;
      return perMilePrice.toFixed(2);
    } else {
      // Flat rate - return plain number without dollar signs/commas
      return finalPrice.toFixed(0);
    }
  };

  // Render lane history charts
  const renderLaneHistoryCharts = () => {
    if (
      !laneHistoryResult ||
      !Object.values(laneHistoryResult.resultsBySource).length
    ) {
      return null;
    }

    return (
      <div>
        {Object.entries(laneHistoryResult.resultsBySource).map(
          ([source, history], chartIndex) => {
            let laneTierHistory = history[0];

            const isTMSChart =
              tmsLaneHistory &&
              tmsLaneHistory.length > 0 &&
              source === tmsLaneHistory[0].source;

            // If there's TMS lane history and multiple tiers, graph the selected tier
            if (
              tmsLaneHistory &&
              isTMSChart &&
              tmsLaneHistorySelectedTierIndex !== null
            ) {
              laneTierHistory = tmsLaneHistory[tmsLaneHistorySelectedTierIndex];
            }

            let laneTierOptions: Undef<LaneTier[]> = undefined;
            if (tmsLaneHistory && isTMSChart) {
              laneTierOptions = tmsLaneHistory
                .filter((history) => Boolean(history.laneTier))
                .map((history) => history.laneTier as LaneTier);
            }

            const yAxisMax = Math.max(
              ...history[0].weeks
                .filter((item) => Boolean(item.maxRate))
                .map((item) => item.maxRate)
            );

            const tooltipElt = (
              inputtedTransportType: string,
              proxiedTransportType: string
            ) => (
              <div className='flex justify-between'>
                <div className='flex items-baseline gap-x-2'>
                  <Tooltip delayDuration={10}>
                    <TooltipTrigger className='border-b border-dashed border-neutral-900 text-sm font-normal'>
                      {`(${titleCase(proxiedTransportType)})`}
                    </TooltipTrigger>
                    <TooltipContent className='mr-1 font-normal max-w-60 whitespace-pre-wrap'>
                      <Typography>{`Triumph does not support ${titleCase(inputtedTransportType)} quotes, showing equivalent ${titleCase(proxiedTransportType)} quote.`}</Typography>
                    </TooltipContent>
                  </Tooltip>
                </div>
              </div>
            );

            let badge: Undef<TierFallbackBadge>;
            if (isTMSChart && tmsLaneHistory) {
              badge = getFallbackTierBadge(
                tmsLaneHistory,
                laneTierHistory.laneTier ?? LaneTier.StateLaneTier
              );
            }

            let chartDescription = '';
            if (chartIndex === 0) {
              chartDescription = laneTierHistory.isPercentile
                ? getLaneHistoryChartDescription({
                    laneTier: laneTierHistory.laneTier,
                    stops: result?.response?.stops,
                    isZipCodeLookup: result?.response?.isZipCodeLookup,
                  })
                : 'Rates from the last four weeks';
            }

            return (
              <div key={`${source}-${chartIndex}`} className='mb-4'>
                <GenericLineChart
                  data={laneTierHistory.weeks}
                  title={`${integrationNameMap[laneTierHistory.source as keyof typeof integrationNameMap]} Lane History`}
                  badge={badge}
                  subtitle={
                    laneTierHistory.inputtedTransportType !==
                    laneTierHistory.proxiedTransportType
                      ? tooltipElt(
                          laneTierHistory.inputtedTransportType,
                          laneTierHistory.proxiedTransportType
                        )
                      : titleCase(laneTierHistory.proxiedTransportType)
                  }
                  // Don't repeat description for each chart
                  description={chartDescription}
                  toggleOptions={laneTierOptions}
                  selectedData={laneTierHistory.laneTier as string}
                  toggleDataHandler={toggleLaneHistoryGraph}
                  chartConfig={
                    laneTierHistory.isPercentile
                      ? chartConfigPercentile
                      : chartConfig
                  }
                  yAxisDomainMax={yAxisMax}
                  yAxisDomainMin={0}
                  yAxisWidth={yAxisMax > 999 ? 45 : 40}
                  thirdTooltipLabel='Loads'
                  dataKeys={
                    [
                      'maxRate',
                      'averageRate',
                      'lowestRate',
                    ] as (keyof WeekLaneData)[]
                  }
                  isPercentile={laneTierHistory.isPercentile}
                />
              </div>
            );
          }
        )}
      </div>
    );
  };

  // Render quote results
  const renderResults = () => {
    if (!result || !result.response) return null;

    const laneQuoteRequestId = result.response.quoteRequestId;

    const calculator = (
      <CarrierPriceCalculator
        calculatorParent={CarrierPriceCalculatorParent.QuickQuote}
        parentQuoteRequestId={result.response.quoteRequestId}
        showTitle={false}
        mileage={maxDistance}
        finalPrice={calculatorFinalPrice}
        fuelEstimate={fuelEstimate}
        datFuelSurcharge={datFuelSurcharge}
        kencoFuelSurcharge={kencoFuelSurcharge}
        customerTableFuelSurcharge={customerTableFuelSurcharge}
        defaultFSCProvider={result.response?.configuration?.fscProvider ?? ''}
        selectedQuoteType={selectedCarrier}
        profit={profit}
        profitType={profitType}
        maxDistance={maxDistance}
        terminatingActionHandler={
          onLaneTerminatingAction
            ? async (action?, quotedValueType?) => {
                const termAction = (action ??
                  QuoteTerminatingAction.Copy) as QuoteTerminatingAction;
                const { flatCarrierCost, finalProfit } = calculatePricing(
                  carrierCost,
                  carrierCostType,
                  profit ?? 0,
                  profitType,
                  maxDistance
                );
                const body = buildUserConfirmedQuoteBody({
                  finalQuotePrice: _.round(finalPrice ?? 0),
                  finalMargin: _.round(finalProfit),
                  marginType: profitType,
                  finalCarrierCost: _.round(flatCarrierCost),
                  carrierCostType,
                  quoteTerminatingAction: termAction,
                  quotedValueType: quotedValueType
                    ? quoteValueTypeToApi(quotedValueType)
                    : undefined,
                });
                // Update quote request suggestion with the status and the final quote the user uses.
                onLaneTerminatingAction({
                  suggestionId: values.suggestionId,
                  finalQuoteData: body,
                  selectedQuickQuoteId: selectedQuickQuoteIdFromCard,
                  action: termAction,
                  quoteRequestId: laneQuoteRequestId,
                  quotedValueType: body.quotedValueType,
                });
              }
            : async () => {}
        }
        setProfitTypeHandler={setProfitTypeHandler}
        carrierCost={carrierCost}
        carrierCostType={carrierCostType}
        setCarrierCostTypeHandler={setCarrierCostType}
        setCarrierCostHandler={setCarrierCost}
        setProfitHandler={setProfitHandler}
        setFuelEstimateHandler={setFuelEstimate}
        setFinalPriceHandler={setFinalPriceFromCalculator}
        selectedQuickQuoteId={selectedQuickQuoteIdFromCard}
        mileageSource={null}
        portalFuelSurchargeSource={null}
        portalFuelSurcharge={null}
      />
    );

    const quoteCards = allQuoteCards.map((card, cardIndex) => (
      <motion.div
        key={card.type}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 * cardIndex }}
      >
        <QuoteCard
          carrier={card}
          isSelected={selectedCarrier === card.type}
          onClick={() => {
            setSelectedCarrier(card.type);
            if (carrierCostType === CarrierCostType.Flat) {
              setCarrierCost(Math.round(card.cost * 100) / 100);
            } else {
              if (card?.costPerMile) {
                setCarrierCost(card?.costPerMile);
              } else {
                setCarrierCostType(CarrierCostType.Flat);
                setCarrierCost(Math.round(card.cost * 100) / 100);
              }
            }
          }}
          lowConfidenceThreshold={
            result.response?.configuration?.lowConfidenceThreshold || 70
          }
          mediumConfidenceThreshold={
            result.response?.configuration?.mediumConfidenceThreshold || 80
          }
          stops={result.response?.stops || []}
        />
      </motion.div>
    ));

    const quoteInfo = (
      <div className='text-xs'>
        <Typography
          variant='body-sm'
          weight='medium'
          className='w-full text-left'
        >
          Quote for
        </Typography>
        <Typography variant='body-xs' className='w-full text-left'>
          {formatStopsDisplay(values.stops)}
        </Typography>
        {maxDistance > 0 && (
          <Typography
            variant='body-xs'
            className='w-full text-left flex items-center gap-2'
          >
            {`Distance: ${_.round(maxDistance)} miles`}
            <Tooltip delayDuration={10}>
              <TooltipTrigger asChild>
                <InfoIcon className='h-3.5 w-3.5' />
              </TooltipTrigger>
              <TooltipContent>
                {distanceSource && (
                  <Typography>
                    {`Distance from ${getMileageSourceLabel(distanceSource)}`}
                  </Typography>
                )}
              </TooltipContent>
            </Tooltip>

            {result?.response?.stops && (
              <Tooltip delayDuration={10}>
                <TooltipTrigger asChild>
                  <MapIcon
                    className='cursor-pointer h-4 w-4 hover:stroke-brand!'
                    onClick={() =>
                      result.response?.stops &&
                      openExternalUrl(
                        constructGoogleMapsUrl(result.response.stops)
                      )
                    }
                  />
                </TooltipTrigger>
                <TooltipContent>View route in Google Maps</TooltipContent>
              </Tooltip>
            )}
          </Typography>
        )}
      </div>
    );

    return (
      <div className='mt-2'>
        <div className='h-px bg-linear-to-r from-transparent via-neutral-300 to-transparent my-2' />
        <div
          className='flex justify-between items-center cursor-pointer p-2 -m-2 hover:scale-[1.01] transition-all duration-200'
          onClick={onToggleExpand}
          onKeyDown={(e) => e.key === 'Enter' && onToggleExpand()}
          role='button'
          tabIndex={0}
          aria-label={
            isExpanded ? 'Collapse quote details' : 'Expand quote details'
          }
        >
          <Flex align='center' gap='sm'>
            <Typography
              variant='body-sm'
              weight='medium'
              className='text-neutral-700'
            >
              {getBatchQuoteCollapsedSellRateLabel(priceFormat, isTql)}
            </Typography>
          </Flex>
          <Flex align='center' gap='sm'>
            <span className='text-sm font-bold text-neutral-900'>
              {formatFinalPriceDisplay(
                finalPrice,
                priceFormat,
                maxDistance,
                fuelEstimate
              )}
            </span>
            {isExpanded ? (
              <ChevronUpIcon className='w-4 h-4 text-neutral-500' />
            ) : (
              <ChevronDownIcon className='w-4 h-4 text-neutral-500' />
            )}
          </Flex>
        </div>
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              key='details'
              {...ANIMATIONS.toggle}
              className='flex flex-col gap-2 mt-2 w-full'
            >
              <div className='h-px bg-linear-to-r from-transparent via-neutral-300 to-transparent' />

              {quoteInfo}
              {quoteCards}

              {/* Lane History Charts */}
              {(isQuoteLaneHistoryEnabled || isTMSLaneHistoryEnabled) && (
                <div className='mt-3'>
                  {laneHistoryResult &&
                  Object.values(laneHistoryResult.resultsBySource).length ? (
                    renderLaneHistoryCharts()
                  ) : (
                    <Flex justify='center' className='py-2'>
                      <Typography
                        variant='body-xs'
                        className='text-neutral-500'
                      >
                        No Lane History Available
                      </Typography>
                    </Flex>
                  )}
                </div>
              )}

              {/* Lane History from Service Chart */}
              {isGetLaneRateFromServiceEnabled &&
                laneHistoryFromServiceResult && (
                  <div className='mt-3'>
                    <LaneHistoryFromServiceChart
                      months={laneHistoryFromServiceResult.months}
                      last7Days={laneHistoryFromServiceResult.last7Days}
                      title='DAT Rate History'
                      subtitle={titleCase(
                        laneHistoryFromServiceResult.equipment
                      )}
                      description='Monthly rate trends with 7-day recent data'
                    />
                  </div>
                )}
            </motion.div>
          )}
        </AnimatePresence>
        <div className={cn('mt-3', !isExpanded && 'hidden')}>{calculator}</div>
      </div>
    );
  };

  // Function that can be called by parent to externally update selected carrier
  const externallySetSelectedCarrier = useCallback(
    (carrierType: SelectedQuoteType) => {
      if (!allQuoteCards?.length) return;
      const card = allQuoteCards.find(
        (c: QuoteCardType) => c.type === carrierType
      );
      if (!card) return;

      setSelectedCarrier(card.type);

      if (carrierCostType === CarrierCostType.Flat) {
        setCarrierCost(Math.round(card.cost * 100) / 100);
      } else if (card?.costPerMile) {
        setCarrierCost(card.costPerMile);
      } else {
        // Fallback to flat rate
        setCarrierCostType(CarrierCostType.Flat);
        setCarrierCost(Math.round(card.cost * 100) / 100);
      }
    },
    [allQuoteCards, carrierCostType]
  );

  // Function that can be called by parent to externally update price format
  const externallySetPriceFormat = useCallback(
    (newPriceFormat: CarrierCostType | 'Both') => {
      setPriceFormat(newPriceFormat);
      if (onPriceFormatChange) {
        onPriceFormatChange(index, newPriceFormat);
      }
    },
    [index, onPriceFormatChange]
  );

  // Register the setter with parent whenever result or state changes
  useEffect(() => {
    if (registerCarrierSetter) {
      registerCarrierSetter(index, externallySetSelectedCarrier);
      // Clean up on unmount
      return () => {
        registerCarrierSetter(index, null as any);
      };
    }
    // Explicitly return undefined so all code paths have a return value
    return undefined;
  }, [registerCarrierSetter, index, externallySetSelectedCarrier]);

  // Register the price format setter with parent
  useEffect(() => {
    if (registerPriceFormatSetter) {
      registerPriceFormatSetter(index, externallySetPriceFormat);
      // Clean up on unmount
      return () => {
        registerPriceFormatSetter(index, null as any);
      };
    }
    // Explicitly return undefined so all code paths have a return value
    return undefined;
  }, [registerPriceFormatSetter, index, externallySetPriceFormat]);

  // Notify parent of initial price format
  useEffect(() => {
    if (onPriceFormatChange) {
      onPriceFormatChange(index, priceFormat);
    }
  }, [onPriceFormatChange, index, priceFormat]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) {
        clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  const renderQuoteError = () => {
    if (!hasQuoteError && !isRetrying) return null;

    return (
      <div className='mt-2'>
        <div className='h-px bg-linear-to-r from-transparent via-neutral-300 to-transparent my-2' />
        <div className='flex justify-between items-center p-2 -m-2'>
          {isRetrying ? (
            <div className='flex items-center gap-1.5'>
              <p className='text-xs font-medium'>Fetching quote ...</p>
            </div>
          ) : (
            <div className='flex items-center gap-1.5'>
              <CircleAlertIcon className='w-3.5 h-3.5 text-error-500 shrink-0' />
              <p className='text-xs font-medium text-error-700'>
                Quote lookup failed
              </p>
            </div>
          )}
          <div className='flex items-center gap-2'>
            {onRetryQuote && (
              <button
                onClick={() => onRetryQuote(index)}
                disabled={isRetrying}
                className={cn(
                  'inline-flex items-center gap-1 text-xs font-medium transition-all duration-200',
                  isRetrying
                    ? 'text-error-500 cursor-not-allowed'
                    : 'text-error-600 hover:text-error-700 hover:underline'
                )}
              >
                {isRetrying ? (
                  <>
                    <ButtonLoader />
                    Retrying...
                  </>
                ) : (
                  <>
                    <RefreshCcwIcon className='w-3 h-3' />
                    Try Again
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <motion.div
      layout
      transition={{ type: 'spring', duration: 0.2, bounce: 0 }}
      {...ANIMATIONS.card}
      className={`overflow-hidden w-full rounded-[4px] border bg-linear-to-br shadow-sm relative transition-all duration-200 p-2.5 ${getBatchQuoteCardBorderClass(
        hasQuoteError,
        isSelected
      )}`}
    >
      <Flex justify='between' align='center' className='mb-2'>
        <Flex align='center' gap='md'>
          <Flex
            align='center'
            justify='center'
            className='relative h-4 w-4 group'
          >
            <Checkbox
              checked={isSelected}
              onCheckedChange={onSelect}
              aria-label={`Select lane ${index + 1}`}
              showCheck={false}
              className={cn(
                'h-5 w-5 bg-neutral-50 hover:scale-105 transition-all duration-200',
                getLaneCheckboxClass(hasQuoteError, isSelected)
              )}
            />
            <span
              className={cn(
                'absolute pointer-events-none text-xs font-medium',
                getLaneIndexLabelClass(hasQuoteError, isSelected)
              )}
            >
              {index + 1}
            </span>
          </Flex>
          {!isEditing &&
            (values?.subTransportType || values?.transportType) && (
              <span
                className={cn(
                  'text-xs font-semibold',
                  hasQuoteError ? 'text-error-700' : 'text-info-600'
                )}
              >
                {getEquipmentLabel()}
              </span>
            )}
        </Flex>

        {/* Action buttons */}
        {renderActions()}
      </Flex>

      <AnimatePresence mode='wait'>
        {isEditing ? renderEditingForm() : renderViewMode()}
      </AnimatePresence>

      {(hasQuoteError || isRetrying) && !isEditing && renderQuoteError()}

      {!hasQuoteError && !isRetrying && renderResults()}
    </motion.div>
  );
};

const ANIMATIONS = {
  card: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
  },
  toggle: {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: { duration: 0.15 },
  },
  actions: {
    initial: { opacity: 0, scale: 0.8 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.8 },
    transition: { duration: 0.1 },
  },
};
